/**
 * Canonical RBAC-A1 Role Vocabulary — Frontend Mirror
 * ====================================================
 *
 * Single source of truth for FE role checks.  Mirrors
 * `backend/services/rbac_service.py:StandardRole` exactly.
 *
 * RBAC-A1 hard contract:
 *   - Backend tokens carry ONLY these canonical role values.
 *   - Legacy role strings (`clinic_admin`, `sales_rep`, `coordinator`,
 *     `doctor`, `admin`, `super_admin`, etc.) are rejected at the
 *     middleware with HTTP 401.
 *   - FE MUST NOT introduce alias maps.  If a stored token carries a
 *     non-canonical role, the FE clears it and redirects to login.
 *
 * Drift guard `frontend/src/__tests__/rbac_fe_canonical_roles.test.js`
 * locks this contract.
 */

// ── TreatX-side (7) ──
export const TREATX_SUPER_ADMIN = 'treatx_super_admin';
export const TREATX_ADMIN = 'treatx_admin';
export const TREATX_COORDINATOR = 'treatx_coordinator';
export const TREATX_SALES_REP = 'treatx_sales_rep';
export const TREATX_AFTER_SALES_REP = 'treatx_after_sales_rep';
export const TREATX_ACCOUNTANT = 'treatx_accountant';
export const TREATX_SUPPORT = 'treatx_support';

// ── Provider-side (7) ──
export const PROVIDER_ADMIN = 'provider_admin';
export const PROVIDER_COORDINATOR = 'provider_coordinator';
export const PROVIDER_SALES_REP = 'provider_sales_rep';
export const PROVIDER_AFTER_SALES_REP = 'provider_after_sales_rep';
export const PROVIDER_ACCOUNTANT = 'provider_accountant';
export const PROVIDER_DOCTOR = 'provider_doctor';
export const PROVIDER_STAFF = 'provider_staff';

// ── Patient + system ──
export const PATIENT = 'patient';
export const API_CLIENT = 'api_client';
export const PENDING = 'pending';  // sentinel — onboarding-only, no permissions

// ── Sets ──
export const TREATX_ROLES = [
  TREATX_SUPER_ADMIN,
  TREATX_ADMIN,
  TREATX_COORDINATOR,
  TREATX_SALES_REP,
  TREATX_AFTER_SALES_REP,
  TREATX_ACCOUNTANT,
  TREATX_SUPPORT,
];

export const PROVIDER_ROLES = [
  PROVIDER_ADMIN,
  PROVIDER_COORDINATOR,
  PROVIDER_SALES_REP,
  PROVIDER_AFTER_SALES_REP,
  PROVIDER_ACCOUNTANT,
  PROVIDER_DOCTOR,
  PROVIDER_STAFF,
];

// TreatX-side admin roles that bypass scope checks.
export const TREATX_ADMIN_ROLES = [TREATX_SUPER_ADMIN, TREATX_ADMIN];

// Provider-side admin role (sole top-level provider admin).
export const PROVIDER_ADMIN_ROLES = [PROVIDER_ADMIN];

// All canonical roles (used for token validation).
export const CANONICAL_ROLES = new Set([
  ...TREATX_ROLES,
  ...PROVIDER_ROLES,
  PATIENT,
  API_CLIENT,
  PENDING,
]);

/**
 * True iff `role` is in the canonical RBAC-A1 vocabulary.
 */
export function isCanonicalRole(role) {
  return typeof role === 'string' && CANONICAL_ROLES.has(role);
}

/**
 * True iff `role` is a TreatX-side admin role
 * (treatx_super_admin or treatx_admin).
 */
export function isTreatxAdminRole(role) {
  return TREATX_ADMIN_ROLES.includes(role);
}

/**
 * True iff `role` is any TreatX-side role.
 */
export function isTreatxRole(role) {
  return TREATX_ROLES.includes(role);
}

/**
 * True iff `role` is any Provider-side role.
 */
export function isProviderRole(role) {
  return PROVIDER_ROLES.includes(role);
}
